/**
 * Virtualised message-list view (iter135.15 — Phase C refactor).
 *
 * Backed by ``react-virtuoso`` so only the visible rows + a small
 * over-render buffer are in the DOM at any time. Switching into a
 * conversation with 1 000 historical messages is now O(visible) in
 * render cost instead of O(history) — measured ~25-40 ms on the
 * preview environment.
 *
 * Edit/recall perf is preserved (each ``MessageBubble`` subscribes to
 * its own message slice via ``useMessage`` so mutating bubble K only
 * re-renders K — unchanged from iter135.11 Phase A).
 *
 * The component owns the scroll container. Parent concerns are wired
 * via props:
 *   • ``innerRef``         — imperative API with ``scrollToBottom()``
 *                           for snap-to-bottom / jump-to-latest.
 *   • ``onStartReached``   — fired when the user scrolls into the
 *                           top ~400 px. Replaces the old
 *                           "scrollTop < 80" paginator trigger.
 *   • ``onAtBottomChange`` — fired with a boolean whenever the
 *                           at-bottom state flips; the page uses
 *                           this for the jump-to-latest button.
 *   • ``scrollerRef``      — ref callback that hands back the
 *                           underlying scroll DOM node so the page
 *                           can attach dragenter/over/leave/drop
 *                           listeners for the file-drop UX.
 *   • ``followOutput``     — 'auto' | 'smooth' | false; Virtuoso
 *                           auto-scrolls to the newest item when
 *                           one is appended AND we're already at
 *                           the bottom.
 *   • ``header`` / ``footer`` / ``emptyComponent`` — slots for the
 *                           existing loading spinner, typing
 *                           indicator, and empty-state card.
 */
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Virtuoso } from "react-virtuoso";

import { useMessageOrder } from "@/lib/conv/hooks";
import { convStore } from "@/lib/conv/store";
import { useT } from "@/lib/i18n";
import { MessageBubble, SystemMessageRow } from "./MessageBubble";

/** Yellow "Unread messages" divider — WhatsApp / Slack style. Sits
 *  above the first unread message at the moment the user opened
 *  the conversation. Does NOT shift as the user reads further — the
 *  anchor is locked for the session on that conversation and cleared
 *  when the user switches away. */
function UnreadDivider() {
  const t = useT();
  return (
    <div
      data-testid="conv-unread-divider"
      className="my-3 flex items-center gap-2"
    >
      <div className="flex-1 h-px" style={{ background: "#fde68a" }} />
      <span
        className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
        style={{ background: "#fde68a", color: "#92400e" }}
      >
        {t("convv2.unread_divider")}
      </span>
      <div className="flex-1 h-px" style={{ background: "#fde68a" }} />
    </div>
  );
}

export const MessageList = forwardRef(function MessageList(
  {
    convId,
    currentUserKey,
    isStaff = false,
    onEditMessage,
    onRecallMessage,
    onStartReached,
    onAtBottomChange,
    unreadAnchorSeq,
    scrollerRef,
    followOutput = true,
    header: HeaderSlot,
    footer: FooterSlot,
    emptyComponent = null,
    className,
    style,
    "data-testid": dataTestId,
  },
  ref,
) {
  const order = useMessageOrder(convId);
  const virtuosoRef = useRef(null);

  // iter136.5 — Virtuoso's "load older above" pattern.
  // ``firstItemIndex`` is a virtual index assigned to ``order[0]``;
  // when older messages are prepended (``order`` grows at the top),
  // we MUST decrement it by the number of new rows so Virtuoso's
  // anchor logic preserves the user's reading position. Without
  // this, the second history page silently fails to load AND the
  // viewport jumps to the absolute top after the first prepend.
  // ``STARTING_INDEX`` just needs to be larger than any conv's
  // expected backlog so we never go negative.
  const STARTING_INDEX = 100_000;
  const [firstItemIndex, setFirstItemIndex] = useState(STARTING_INDEX);
  const prevOrderRef = useRef([]);
  const prevConvIdRef = useRef(convId);

  useLayoutEffect(() => {
    const prev = prevOrderRef.current;
    if (prevConvIdRef.current !== convId) {
      // Conversation switch — reset bookkeeping so the new conv
      // starts at STARTING_INDEX and Virtuoso re-anchors correctly.
      prevConvIdRef.current = convId;
      prevOrderRef.current = order;
      setFirstItemIndex(STARTING_INDEX);
      return;
    }
    if (
      order.length > prev.length
      && prev.length > 0
      && order[order.length - 1] === prev[prev.length - 1]
    ) {
      // Items were prepended above — last id is unchanged. Decrement
      // the virtual offset by how many new rows landed at the top.
      const delta = order.length - prev.length;
      setFirstItemIndex((idx) => idx - delta);
    }
    prevOrderRef.current = order;
  }, [order, convId]);
  // iter136.2 — keep our own handle on the underlying scroll DOM so
  // ``scrollToBottom`` can push past the footer slot (typing indicator
  // + bottom spacer). Virtuoso's ``scrollToIndex({align:"end"})``
  // alone aligns the LAST ITEM with the viewport bottom, leaving the
  // footer hanging below — which on screen looks like the button
  // scrolled to the second-to-last message.
  const innerScrollerRef = useRef(null);
  const composedScrollerRef = (node) => {
    innerScrollerRef.current = node;
    if (typeof scrollerRef === "function") scrollerRef(node);
    else if (scrollerRef) scrollerRef.current = node;
  };

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom: (behavior = "auto") => {
        const v = virtuosoRef.current;
        if (!v || order.length === 0) return;
        v.scrollToIndex({
          // iter136.5 — absolute virtual index when ``firstItemIndex``
          // is set. Use ``LAST`` sentinel which works regardless.
          index: "LAST",
          align: "end",
          behavior,
        });
        // Follow up on the next frame: nudge the raw scroller to its
        // absolute bottom so the FooterSlot doesn't push the last
        // bubble out of view.
        requestAnimationFrame(() => {
          const sc = innerScrollerRef.current;
          if (sc) sc.scrollTop = sc.scrollHeight;
        });
      },
    }),
    [order.length],
  );

  // Stable item renderer — reading ``convStore.getMessage`` here does
  // NOT subscribe (the subscription lives inside the bubble). The
  // order-array change is the only thing that invalidates the list.
  //
  // iter135.19 — horizontal padding is applied INSIDE the item
  // wrapper (not on the Virtuoso scroller) so the internal list
  // width matches the scroller exactly → no phantom horizontal
  // scrollbar on narrow viewports.
  //
  // iter135.20 — when the rendered message's ``sequence_number``
  // equals ``unreadAnchorSeq`` we prepend an "N new messages"
  // divider to the item. This keeps the divider locked in the list
  // (doesn't shift as the user reads) without requiring a separate
  // data stream.
  //
  // iter135.22 — Slack-style follow-up grouping. We mark a bubble
  // as a "follow-up" when the immediately preceding NON-system
  // message has the same sender AND was sent ≤60 s earlier. The
  // bubble drops its sender header, tightens its top padding, and
  // squares the corner that points toward the predecessor so the
  // run reads as a single block.
  const FOLLOWUP_WINDOW_MS = 60_000;
  const itemContent = useMemo(
    () => (absIndex, id) => {
      const m = convStore.getMessage(convId, id);
      if (!m) return null;
      const isSystem = m.content_type === "system" || m.event_type;
      // iter136.5 — Virtuoso passes absolute indices when
      // ``firstItemIndex`` is set; convert back to the data-array
      // position for predecessor lookup.
      const dataIdx = absIndex - firstItemIndex;
      // iter136.2 — DO NOT skip system messages here. If the
      // immediate predecessor is a system event (share started,
      // recall, member join, …), the next bubble starts a NEW
      // visual run — its avatar should reappear so the user sees
      // who is speaking after the interruption.
      let isFollowUp = false;
      if (!isSystem && dataIdx > 0) {
        const prev = convStore.getMessage(convId, order[dataIdx - 1]);
        const prevSystem = !!(prev && (prev.content_type === "system" || prev.event_type));
        if (prev && !prevSystem) {
          const sameSender =
            prev.sender?.user_type === m.sender?.user_type
            && prev.sender?.user_id === m.sender?.user_id;
          if (sameSender && m.server_timestamp && prev.server_timestamp) {
            const a = new Date(m.server_timestamp).getTime();
            const b = new Date(prev.server_timestamp).getTime();
            isFollowUp = Number.isFinite(a) && Number.isFinite(b)
              && Math.abs(a - b) <= FOLLOWUP_WINDOW_MS;
          }
        }
      }
      const body = isSystem ? (
        <SystemMessageRow convId={convId} msgId={id} isStaff={isStaff} />
      ) : (
        <MessageBubble
          convId={convId}
          msgId={id}
          currentUserKey={currentUserKey}
          isMine={currentUserKey
            && `${m.sender?.user_type}:${m.sender?.user_id}` === currentUserKey}
          isFollowUp={isFollowUp}
          onEdit={(currentUserKey
            && `${m.sender?.user_type}:${m.sender?.user_id}` === currentUserKey)
            ? (newContent) => onEditMessage(id, newContent) : null}
          onRecall={(currentUserKey
            && `${m.sender?.user_type}:${m.sender?.user_id}` === currentUserKey)
            ? () => onRecallMessage(id) : null}
        />
      );
      const showDivider = unreadAnchorSeq != null
        && m.sequence_number === unreadAnchorSeq;
      // iter135.21 — vertical rhythm: Virtuoso renders each item as
      // its own sibling div in its list, so the old ``space-y-1``
      // from the pre-virtualised parent no longer applies. We give
      // each item a small breathing gap here. iter135.22 reduces
      // the gap on follow-ups so a stacked run reads as one block.
      const pb = isFollowUp ? "pb-px" : "pb-1";
      return (
        <div className={`px-4 sm:px-12 ${pb}`}>
          {showDivider && <UnreadDivider />}
          {body}
        </div>
      );
    },
    [convId, currentUserKey, isStaff, onEditMessage, onRecallMessage, unreadAnchorSeq, order, firstItemIndex],
  );

  // Empty state — Virtuoso would render a zero-height scroller,
  // which hides the empty-state card. Render the empty slot
  // ourselves in that case.
  if (order.length === 0) {
    return (
      <div
        className={className}
        style={style}
        data-testid={dataTestId}
        ref={scrollerRef}
      >
        {HeaderSlot ? <HeaderSlot /> : null}
        {emptyComponent}
        {FooterSlot ? <FooterSlot /> : null}
      </div>
    );
  }

  const components = {};
  if (HeaderSlot) components.Header = HeaderSlot;
  if (FooterSlot) components.Footer = FooterSlot;

  return (
    <Virtuoso
      ref={virtuosoRef}
      data={order}
      firstItemIndex={firstItemIndex}
      computeItemKey={(_index, id) => id}
      itemContent={itemContent}
      followOutput={followOutput}
      startReached={onStartReached}
      atBottomStateChange={onAtBottomChange}
      // Keep a small buffer above/below the viewport so images that
      // extend the row height don't cause jitter as the user scrolls.
      increaseViewportBy={{ top: 400, bottom: 400 }}
      // iter136.5 — absolute initial index = firstItemIndex + last
      // data position. Pinning this here would freeze the prop on
      // re-renders BUT Virtuoso reads it only at mount, so the
      // expression is safe.
      initialTopMostItemIndex={firstItemIndex + order.length - 1}
      components={components}
      scrollerRef={composedScrollerRef}
      className={className}
      style={style}
      data-testid={dataTestId}
    />
  );
});
